import type { ChangeEvent } from "react";
import { useInput } from "react-admin";
import { Box, Button, Stack, Typography } from "@mui/material";

// Фоновое изображение карты (градиент + арт запечены): загрузка файла сжимается в canvas
// (макс. ширина 1200, JPEG 0.85) и кладётся data-URI в поле image.
async function compress(file: File, maxW = 1200, quality = 0.85): Promise<string> {
  const dataUrl = await new Promise<string>((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result as string);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
  const img = await new Promise<HTMLImageElement>((res, rej) => {
    const i = new Image();
    i.onload = () => res(i);
    i.onerror = rej;
    i.src = dataUrl;
  });
  const scale = Math.min(1, maxW / img.width);
  const w = Math.round(img.width * scale);
  const h = Math.round(img.height * scale);
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) return dataUrl;
  ctx.drawImage(img, 0, 0, w, h);
  return canvas.toDataURL("image/jpeg", quality);
}

export function CardImageInput() {
  const { field } = useInput({ source: "image" });
  const value: string = field.value ?? "";

  const onFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      field.onChange(await compress(f));
    } catch {
      /* ignore */
    }
    e.target.value = "";
  };

  return (
    <Stack spacing={1} sx={{ my: 1, width: "100%", maxWidth: 520 }}>
      <Typography variant="caption" color="text.secondary">
        Изображение карты (фон панели: градиент + арт запечены; текст рисуется поверх)
      </Typography>
      <Box
        sx={{
          width: 300,
          aspectRatio: "2.4",
          borderRadius: 2,
          bgcolor: "action.hover",
          display: "grid",
          placeItems: "center",
          overflow: "hidden",
          color: "text.disabled",
        }}
      >
        {value ? <img src={value} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : "—"}
      </Box>
      <Stack direction="row" spacing={2}>
        <Button variant="outlined" component="label" size="small">
          Загрузить изображение
          <input type="file" accept="image/*" hidden onChange={onFile} />
        </Button>
        {value && (
          <Button size="small" color="inherit" onClick={() => field.onChange("")}>
            Очистить
          </Button>
        )}
      </Stack>
    </Stack>
  );
}
